import platform from 'platform'
import {logger, Util} from 'yougile-util'
import {DataObjectId, Hub, User} from 'yougile-model'
import {SharedBoardsClient} from '@client/shared-boards-client'
import {Backend} from '@common/backend'
import {Accounts} from '../client/accounts'
import {Connector} from '../client/connector'
import {PaymentClient} from '../client/payment-client'
import {Sync} from '../client/sync'
import {MessageBox} from '../components/message-box'
import {Board} from './board'
import {ChatSubscribers} from './chat-subscribers'
import {Column} from './column'
import {globals} from './globals-base'
import {News} from './news'
import {Project} from './project'
import {SeenBy} from './seen-by'
import {Task} from './task'

const feedbackTicketStateProp = 'feedbackTicketState'
const feedbackDoneColumnProp = 'feedbackDoneGroup'

/**
 * Additional user data attached to a ticket
 */
export interface FeedbackUserData {
  id: DataObjectId
  name: string
  email: string
  isMobile: boolean
  browser: string
  os: string
}

const feedbackUserEmailProp = 'feedbackUserEmail'
const feedbackUserNameProp = 'feedbackUserName'
const feedbackUserIdProp = 'feedbackUserId'
const feedbackIsMobileProp = 'feedbackIsMobile'
const feedbackBrowserProp = 'feedbackBrowser'
const feedbackOsProp = 'feedbackOs'
const feedbackTariff = 'feedbackTariff'
const feedbackUserCount = 'feedbackUserCount'

export class Feedback {
  public static stats: {
    timestamp: number
    result: unknown
  }

  private static statByUser: Map<DataObjectId, unknown> = new Map()

  public static column: Hub
  public static project: Hub
  public static board: Hub

  /**
   * Prepares feedback project if absent
   */
  public static init(): void {
    if (SharedBoardsClient.getId()) {
      return
    }
    try {
      const prof = Sync.getProfile()
      const cmp = prof.get(prof.companyId)
      const prj = prof.findHubByTitle('Feedback', cmp.id, true)

      Feedback.project =
        prj ?? Project.createNew(prof.companyId, 'Feedback', false)
      Feedback.board =
        prof.findHubByTitle('Feedback', Feedback.project.id, true) ??
        Project.addBoard('Feedback', Feedback.project, false)
      Feedback.column =
        prof.findHubByTitle('Tickets', Feedback.board.id, true) ??
        Board.addColumn('Tickets', Feedback.board)
    } catch (e) {
      logger.error('Error while creating feedback project', e)
      Backend.report('Error while creating feedback project')
    }
  }

  public static getUserStat(id: DataObjectId): unknown {
    return Feedback.statByUser.get(id)
  }

  public static setFeedbackUserData(ticket: Hub): void {
    const user = Sync.getProfile().get(globals.currentUserId) as User
    const acc = Accounts.get(user.id)
    ticket.properties[feedbackUserIdProp] = user.id
    ticket.properties[feedbackUserNameProp] = acc.realName
    ticket.properties[feedbackUserEmailProp] = user.email
    ticket.properties[feedbackIsMobileProp] = false
    ticket.properties[feedbackBrowserProp] =
      platform.name + ' ' + platform.version
    ticket.properties[feedbackOsProp] = platform.os?.toString() ?? ''
    const payment = PaymentClient.get(globals.getCompanyId())
    if (payment) {
      ticket.properties[feedbackTariff] = payment.tariff
    }
    const box = Sync.getCurrentSandbox()
    if (box) {
      ticket.properties[feedbackUserCount] = box.getAllUsers(false).length
    }
    ticket.commit()
  }

  public static getFeedbackUserData(ticket: Hub): FeedbackUserData | undefined {
    if (
      ticket.companyId === Sync.profileId ||
      !ticket.properties[feedbackUserIdProp]
    ) {
      return undefined
    }
    return {
      id: ticket.properties[feedbackUserIdProp] as DataObjectId,
      name: ticket.properties[feedbackUserNameProp] as string,
      email: ticket.properties[feedbackUserEmailProp] as string,
      isMobile: ticket.properties[feedbackIsMobileProp] as boolean,
      browser: (ticket.properties[feedbackBrowserProp] as string) || '?',
      os: (ticket.properties[feedbackOsProp] as string) || '?'
    }
  }

  public static createTicket(title: string, message: string): void {
    const ticket = Task.createTask(title, {
      location: Feedback.column.id,
      companyId: Feedback.column.companyId,
      resolveNameConflicts: true
    })
    ticket.chat.addMessage(globals.currentUserId, {text: message})
    ticket.commit()
    Feedback.setFeedbackUserData(ticket)
    SeenBy.markSeen(ticket, globals.currentUserId)
    Sync.onServerApprovedCreation(Sync.profileId, ticket.id, () => {
      Connector.request('add-feedback-ticket', {
        companyId: Sync.profileId,
        taskId: ticket.id
      }).catch(res => {
        MessageBox.show(res.error, 'danger')
      })
    })
    if (!ChatSubscribers.isSubscribed(globals.currentUserId, ticket)) {
      ChatSubscribers.subscribe(globals.currentUserId, ticket)
    }
  }

  public static isTicketClosed(task: Hub): boolean {
    return task.properties[feedbackTicketStateProp] === 'closed'
  }

  public static closeTicket(task: Hub): void {
    task.properties[feedbackTicketStateProp] = 'closed'
    task.commit()
  }

  public static openTicket(task: Hub): void {
    task.properties[feedbackTicketStateProp] = 'open'
    task.commit()
  }

  public static isFeedbackDoneColumn(col: Hub): boolean {
    return !!col.properties[feedbackDoneColumnProp]
  }

  public static setFeedbackDoneColumn(col: Hub): void {
    col.properties[feedbackDoneColumnProp] = true
    col.commit()
  }

  public static tickets(): Hub[] {
    return Column.listTasks(Feedback.column, false).sort(
      (first: Hub, second: Hub) => second.timestamp - first.timestamp
    )
  }

  public static openTickets(): Hub[] {
    return Feedback.tickets().filter(Util.negate(Feedback.isTicketClosed))
  }

  public static closedTickets(): Hub[] {
    return Feedback.tickets().filter(Feedback.isTicketClosed)
  }

  public static newMessagesAmount(user: User): number {
    const {id, timestamp} = user

    return Feedback.tickets().reduce((sum: number, ticket: Hub) => {
      return sum + ticket.chat.newMessagesFor(id, timestamp)
    }, 0)
  }

  public static getNewItemCount(): number {
    let count = 0
    const profile = Sync.getProfile()
    const timestamp = profile.get(profile.companyId).timestamp
    Column.listTasks(Feedback.column, false).forEach(t => {
      count += t.chat.newMessagesFor(globals.currentUserId, timestamp)
    })
    count += News.list().filter(News.isUnseen).length
    return count
  }
}
